/**
 * Display adapter for a cell's or title's source string.
 *
 * Receives the UNTOUCHED source and returns only display. Nothing produced
 * here is ever read back: sorting, filtering and the xlsx export all read the
 * table model, so a cell can show a mark while a filter typed with the emoji
 * still finds the row.
 *
 * THE SEAM IS THE WHOLE DESIGN PROBLEM. An emoji is a character — it sits on
 * the baseline and owns the word space after it. A Lucide icon is a 24x24 box
 * whose artwork stops short of the edges, so at 1em it already carries roughly
 * 0.167em of air per side. Substitute one for the other and keep the authored
 * space and the gap lands near 0.42em: an icon standing NEXT TO the words
 * rather than set WITH them, which is the tell that separates typesetting from
 * icon injection.
 *
 * So a mapped glyph consumes the single space it inherited and re-sets it as a
 * ~0.15em margin. Space BEFORE a glyph is left alone: that one belongs to the
 * sentence and is doing real separation work, while the one after belongs to
 * the label the emoji forms with the word it precedes. Tightening the sentence
 * side would run clauses together.
 *
 * Eating a source character is safe precisely because the source is untouched:
 * export, copy and filter never see this string, and the streaming parity
 * signature compares collapsed whitespace, so the React-children adapter and
 * this one agree either way.
 */
import { Fragment, type ReactNode } from "react";

import { EditorialGlyph } from "./editorial-glyph";
import { CURRENCY_TOKEN_RE } from "./utils/cell-detection";
import type {
    EditorialGlyphUsage,
    EditorialSemantic,
} from "./utils/editorial-policy";
import {
    resolveRemovalSeam,
    scanEditorialText,
} from "./utils/emoji-typesetting";

export interface EditorialInlineTextProps {
    /** The untouched source string. */
    readonly text: string;
    /** Selects the glyph's functional label; cells read as `table-cell`. */
    readonly usage?: EditorialGlyphUsage;
    readonly precedingText?: string;
    readonly followingText?: string;
    readonly consumeLeadingSpace?: boolean;
}

/**
 * Currency bolding over ONE text run, keyed by absolute source offset.
 *
 * Runs only over text the emoji pass already resolved, never over generated
 * elements — that ordering is what keeps the composition single-pass.
 */
const withCurrency = (text: string, offset: number): ReactNode => {
    if (!text.includes("$")) return text;

    const parts = text.split(CURRENCY_TOKEN_RE);
    if (parts.length === 1) return text;

    const nodes: ReactNode[] = [];
    let at = offset;
    for (let index = 0; index < parts.length; index++) {
        const part = parts[index];
        if (part) {
            nodes.push(
                index % 2 === 1 ? (
                    <span key={at} className="font-semibold tabular-nums">
                        {part}
                    </span>
                ) : (
                    <Fragment key={at}>{part}</Fragment>
                )
            );
        }
        at += part.length;
    }
    return nodes;
};

type DisplayItem =
    | { kind: "text"; value: string; at: number }
    | {
          kind: "glyph";
          semantic: EditorialSemantic;
          source: string;
          at: number;
          tight: boolean;
      };

/**
 * Shorten already-emitted text so it ends at `offset`.
 *
 * A removal's seam is measured against the ORIGINAL string and can reach back
 * across whitespace this walk has already emitted, so the trailing text is
 * rewound rather than predicted.
 */
const trimTo = (items: DisplayItem[], offset: number): void => {
    while (items.length > 0) {
        const last = items[items.length - 1];
        if (last.kind !== "text") return;

        if (last.at + last.value.length <= offset) return;
        if (last.at >= offset) {
            items.pop();
            continue;
        }
        last.value = last.value.slice(0, offset - last.at);
        return;
    }
};

export function EditorialInlineText({
    text,
    usage = "table-cell",
    precedingText = "",
    followingText = "",
    consumeLeadingSpace = false,
}: EditorialInlineTextProps) {
    const source =
        consumeLeadingSpace && text.startsWith(" ") ? text.slice(1) : text;
    const scan = scanEditorialText(source);

    // Emoji-free prose is the common case and never allocates a segment list.
    if (!scan.changed) return <>{withCurrency(source, 0)}</>;

    const items: DisplayItem[] = [];
    // Source offset to resume text from, after a seam or a consumed space.
    let resumeAt = -1;

    for (const segment of scan.segments) {
        if (segment.kind === "text") {
            let value = segment.source;
            let at = segment.start;
            if (resumeAt > at) {
                const cut = Math.min(resumeAt, segment.end) - at;
                value = value.slice(cut);
                at += cut;
            }
            if (value) items.push({ kind: "text", value, at });
            continue;
        }

        if (segment.disposition === "PRESERVE") {
            items.push({
                kind: "text",
                value: segment.source,
                at: segment.start,
            });
            continue;
        }

        if (segment.disposition === "DECORATIVE") {
            // Delegated rather than restated: one copy of "strip the
            // whitespace either side, then decide whether a space goes back".
            const seam = resolveRemovalSeam(source, segment.start, segment.end);
            trimTo(items, seam.start);
            let replacement = seam.replacement;
            const externalBefore = seam.start === 0 ? precedingText : "";
            const externalAfter =
                seam.end === source.length ? followingText : "";
            if (
                (externalBefore || externalAfter) &&
                !/\s$/u.test(externalBefore) &&
                !/^\s/u.test(externalAfter)
            ) {
                const contextual = `${externalBefore}${source}${externalAfter}`;
                replacement = resolveRemovalSeam(
                    contextual,
                    externalBefore.length + segment.start,
                    externalBefore.length + segment.end
                ).replacement;
            }
            if (replacement) {
                items.push({
                    kind: "text",
                    value: replacement,
                    at: seam.start,
                });
            }
            resumeAt = seam.end;
            continue;
        }

        // SEMANTIC without a semantic is a manifest this build cannot read.
        // Showing the author's character beats showing nothing.
        if (segment.semantic === undefined) {
            items.push({
                kind: "text",
                value: segment.source,
                at: segment.start,
            });
            continue;
        }

        const tight =
            source[segment.end] === " " ||
            (segment.end === source.length && followingText.startsWith(" "));
        items.push({
            kind: "glyph",
            semantic: segment.semantic,
            source: segment.source,
            at: segment.start,
            tight,
        });
        if (tight) resumeAt = segment.end + 1;
    }

    return (
        <>
            {items.map((item) =>
                item.kind === "text" ? (
                    <Fragment key={item.at}>
                        {withCurrency(item.value, item.at)}
                    </Fragment>
                ) : (
                    // Keyed by source offset, not array index: appending to a
                    // cell must not remount a glyph that has not moved.
                    <span
                        key={item.at}
                        className={item.tight ? "mr-[0.15em]" : undefined}
                    >
                        <EditorialGlyph semantic={item.semantic} usage={usage}>
                            {item.source}
                        </EditorialGlyph>
                    </span>
                )
            )}
        </>
    );
}
